/**
 * Project dev environments: selects which project environment API calls target.
 *
 * The active project is carried in the `fmh-project` cookie. A `?project=` query
 * param sets it (so links are shareable) — `?project=none` clears it. When a
 * project is active, API calls target `<base>/proj/<name>` on the shared dev
 * gateway, where a per-project ingress routes claimed service prefixes to that
 * project's namespace and everything else falls through to shared dev.
 *
 * The same cookie is read by the frontend-host router to serve a project's
 * frontend bundle, so one selection switches both planes. Reads happen at
 * module load: any change to the selection is applied with a full reload.
 */

/**
 * Project environments exist only in dev, so every entry point below is inert
 * anywhere else. Without this gate a `?project=x` link opened against tst, stg
 * or prd would set the cookie, show the badge and prefix API calls with
 * /proj/x — project behaviour leaking into an environment that has no project
 * namespaces and no router to serve them.
 *
 * REACT_APP_ENV comes from the image's ENV build arg (`dev`, `tst`, `stg`,
 * `prd`) and is `dev` for local development and for a project's own frontend
 * build, so both keep working.
 */
const PROJECTS_ENABLED = process.env.REACT_APP_ENV === 'dev';

const PROJECT_COOKIE = 'fmh-project';
const PREFIXES_COOKIE = 'fmh-project-prefixes';
const PROJECT_QUERY_PARAM = 'project';
const PREFIXES_QUERY_PARAM = 'prefixes';
const CLEAR_SENTINEL = 'none';
// Kubernetes-namespace-safe: lowercase alphanumerics and dashes only
const VALID_PROJECT_NAME = /^[a-z0-9][a-z0-9-]{0,38}[a-z0-9]$/;
// Service path prefixes as they appear in URLs (e.g. user-support, payer_request)
const VALID_PREFIX = /^[a-z0-9][a-z0-9_-]{0,38}$/;

const readCookie = (cookie) => {
    const match = new RegExp(`(?:^|; )${cookie}=([^;]*)`).exec(document.cookie);
    return match ? decodeURIComponent(match[1]) : undefined;
};

const writeCookie = (cookie, value) => {
    if (value) {
        document.cookie = `${cookie}=${encodeURIComponent(value)}; path=/; max-age=86400; SameSite=Lax`;
    } else {
        document.cookie = `${cookie}=; path=/; max-age=0; SameSite=Lax`;
    }
};

const parsePrefixes = (raw) =>
    (raw ?? '')
        .split(',')
        .map((prefix) => prefix.trim())
        .filter((prefix) => VALID_PREFIX.test(prefix));

/** Apply `?project=` / `?prefixes=` query params to the cookies before anything reads them. */
/**
 * Reload the current page with the project query params removed.
 *
 * Deliberately not `location.assign`/`location.replace` with a URL built from
 * `location.href`: that is the textbook client-side open-redirect shape and
 * static analysis flags it, even though the target here is only ever the page
 * we are already on. `history.replaceState` cannot leave the origin, and
 * `reload()` takes no argument at all, so neither is a redirect sink. The
 * address bar is cleaned first, then the reloaded request is for that cleaned
 * URL — which is what stops the caller looping on its own query param.
 */
const reloadWithoutProjectParams = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(PROJECT_QUERY_PARAM);
    url.searchParams.delete(PREFIXES_QUERY_PARAM);
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    window.location.reload();
};

const applyQueryParams = () => {
    if (!PROJECTS_ENABLED) {
        return;
    }
    const params = new URLSearchParams(window.location.search);
    const project = params.get(PROJECT_QUERY_PARAM);
    if (project === null) {
        return;
    }
    const previous = readCookie(PROJECT_COOKIE);
    const clearing = project === CLEAR_SENTINEL || project === '';

    if (clearing) {
        writeCookie(PROJECT_COOKIE, undefined);
        writeCookie(PREFIXES_COOKIE, undefined);
    } else if (VALID_PROJECT_NAME.test(project)) {
        writeCookie(PROJECT_COOKIE, project);
        const prefixes = params.get(PREFIXES_QUERY_PARAM);
        if (prefixes !== null) {
            writeCookie(PREFIXES_COOKIE, parsePrefixes(prefixes).join(','));
        } else if (project !== previous) {
            // Switching projects: the previous project's claim list does not
            // apply to the new one, and keeping it would prefix the wrong
            // services. Cleared so the list is re-fetched below.
            writeCookie(PREFIXES_COOKIE, undefined);
        }
    } else {
        return;
    }

    // The page running this code was served by the router using the PREVIOUS
    // cookie, so changing the selection needs one reload for the new project's
    // frontend build to arrive. Without it the old bundle keeps running while
    // the badge and API paths already say the new project — a confusing
    // half-state. The query params are stripped first, so the reloaded page
    // takes the early return above and cannot loop.
    if ((previous ?? '') !== (clearing ? '' : project)) {
        reloadWithoutProjectParams();
    }
};

applyQueryParams();

const activeProject = (() => {
    if (!PROJECTS_ENABLED) {
        return undefined;
    }
    const value = readCookie(PROJECT_COOKIE);
    return value && VALID_PROJECT_NAME.test(value) ? value : undefined;
})();

/** Service prefixes the project's ingress claims — only these get /proj-prefixed. */
const activePrefixes = activeProject ? parsePrefixes(readCookie(PREFIXES_COOKIE)) : [];

// Prefixes unknown (picker selection, expired cookie, bare ?project= link):
// fetch the provisioner-published claim list once and reload with it. The
// sessionStorage guard prevents a reload loop when the endpoint is absent.
if (activeProject && activePrefixes.length === 0) {
    const fetchGuard = `fmh-prefixes-fetched:${activeProject}`;
    if (!sessionStorage.getItem(fetchGuard)) {
        sessionStorage.setItem(fetchGuard, '1');
        void (async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BASE_URL}/proj/${activeProject}/_prefixes`,
                );
                const list = response.ok ? await response.json() : [];
                const prefixes = parsePrefixes(Array.isArray(list) ? list.join(',') : '');
                if (prefixes.length) {
                    writeCookie(PREFIXES_COOKIE, prefixes.join(','));
                    window.location.reload();
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
            }
        })();
    }
}

/** The project selected for this page load, if any. */
export const getProjectsEnabled = () => PROJECTS_ENABLED;

export const getActiveProject = () => activeProject;

/** Select a project (or clear with undefined) and reload so every layer re-reads it. */
export const setActiveProject = (name, prefixes) => {
    if (!PROJECTS_ENABLED) {
        return;
    }
    if (name && !VALID_PROJECT_NAME.test(name)) {
        // eslint-disable-next-line no-console
        console.warn(`projectContext: rejected invalid project name "${name}"`);
        return;
    }
    // eslint-disable-next-line no-console
    console.info(
        `projectContext: ${name ? `selecting project "${name}"` : 'clearing project'} and reloading`,
    );
    writeCookie(PROJECT_COOKIE, name);
    writeCookie(
        PREFIXES_COOKIE,
        name && prefixes?.length ? parsePrefixes(prefixes.join(',')).join(',') : undefined,
    );
    if (name && !prefixes?.length) {
        // eslint-disable-next-line no-console
        console.info(
            `projectContext: no prefixes given for "${name}" — will fetch the claim list from /_prefixes after reload.`,
        );
    }
    reloadWithoutProjectParams();
};

/** The claimed service prefixes for this page load. */
export const getActivePrefixes = () => activePrefixes;

/**
 * Prefix a service URL with /proj/<name> when the active project claims its
 * first path segment; unclaimed services keep bare paths (shared dev serves
 * them — the fall-through). Pass URLs like '/performance' or '/user-support/x'.
 */
export const withProjectPath = (url) => {
    if (!activeProject) {
        return url;
    }
    const first = url.replace(/^\//, '').split('/')[0];
    return activePrefixes.includes(first) ? `/proj/${activeProject}${url}` : url;
};

// Local-mode guard: empty base URL requires the vite proxy (LOCAL_SERVICES).
// Without it every API call returns index.html and login force-loops.
if (!process.env.REACT_APP_BASE_URL) {
    void (async () => {
        try {
            const r = await fetch('/authorization/authentication_info/marketplace/dev', {
                method: 'GET',
            });
            if ((r.headers.get('content-type') ?? '').includes('text/html')) {
                // eslint-disable-next-line no-console
                console.error(
                    'projectContext: REACT_APP_BASE_URL is empty but the dev proxy is not routing — ' +
                        'restart the dev server with the LOCAL_SERVICES env var set, ' +
                        'or delete .env.development.local.',
                );
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    })();
}

/** API base URL. Project scoping is per-path (withProjectPath), not per-base. */
export const getApiBaseUrl = () => process.env.REACT_APP_BASE_URL;
